import { faEdit } from "@fortawesome/pro-regular-svg-icons/faEdit";
import { faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons/faExclamationTriangle";
import { faFlagCheckered } from "@fortawesome/pro-regular-svg-icons/faFlagCheckered";
import * as api from "api/Core";
import { AppMessageBox } from "components/AppMessageBox";
import { SpinnerIndicator } from "components/CallServer";
import DashListBox, { DashBoxListItem } from "components/Dashboard/DashListBox";
import { JoinComponents } from "components/JoinComponents";
import MuiContainer from "components/MuiExtras/MuiContainer";
import LibUtils from "lib/LibUtils";
import { DashboardKnowledgeTestAnalysisFilter, DashboardKnowledgeTestAnalysisResumeModel, DashboardKnowledgeTestAnalysisSummaryColumn } from "models/ApiModels";
import * as React from "react";
import { WithTranslation } from "react-i18next";

/////////////////////////////////////////////////////////////////////////////////////

interface ITileProps {
    activeTile: DashboardKnowledgeTestAnalysisSummaryColumn;
    filter: DashboardKnowledgeTestAnalysisFilter;
    onChange: (tile: DashboardKnowledgeTestAnalysisSummaryColumn) => void;
}

interface TileState {
    data: DashboardKnowledgeTestAnalysisResumeModel;
    loading: boolean;
}

type Props = ITileProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class KnowledgeTestAnalysisTiles extends React.PureComponent<Props, TileState>
{
    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            loading: true,
        };
    }

    ///
    private loadData() {
        api.getDashboardKnowledgeTestAnalysis(
            {
                columnToSummary: DashboardKnowledgeTestAnalysisSummaryColumn.Resume,
                ...this.props.filter
            })
            .then((result: DashboardKnowledgeTestAnalysisResumeModel) => {
                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    ///
    private handleTileClick(tile: DashboardKnowledgeTestAnalysisSummaryColumn) {
        this.props.onChange(tile);
    }

    ///
    private renderBoxes() {
        const { data, loading } = this.state;
        const { activeTile } = this.props;

        var boxes: DashBoxListItem[] = [
            {
                key: DashboardKnowledgeTestAnalysisSummaryColumn.Published,
                title: loading ? <SpinnerIndicator /> : data.totalPublished.toLocaleString(),
                subtitle: this.props.t("Views.Dashboards.KnowledgeTestAnalysis.Tiles.PublishedSubtitle"),
                description: loading ? "" : `${data.totalPublished30.toLocaleString()} ${this.props.t("Views.Dashboards.Common.InLastMonth").toLowerCase()}`,
                icon: faEdit,
            },
            {
                key: DashboardKnowledgeTestAnalysisSummaryColumn.Answered,
                title: loading ? <SpinnerIndicator /> : `${data.totalAnswered}%`,
                subtitle: <div><JoinComponents components={this.props.t("Views.Dashboards.KnowledgeTestAnalysis.Tiles.AnsweredSubtitle").split('\n')} separator={<br />} /></div>,
                icon: faFlagCheckered,
            },
            {
                key: DashboardKnowledgeTestAnalysisSummaryColumn.MissingTests,
                title: loading ? <SpinnerIndicator /> : data.topicsMissingTests.toLocaleString(),
                subtitle: <div><JoinComponents components={this.props.t("Views.Dashboards.KnowledgeTestAnalysis.Tiles.MissingTestsSubtitle").split('\n')} separator={<br />} /></div>,
                icon: faExclamationTriangle,
                contentColor: !loading && data.topicsMissingTests > 0 ? "warning" : null
            },
        ];

        return (
            <>
                <DashListBox
                    color="success"
                    boxes={boxes}
                    active={activeTile}
                    onClick={
                        (key: DashboardKnowledgeTestAnalysisSummaryColumn) => {
                            if (key != activeTile) this.handleTileClick(key);
                        }
                    }
                />
            </>
        );
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (!LibUtils.isEqual(prevProps.filter, this.props.filter)) {
            this.loadData();
        }
    }

    ///
    public render() {
        const { data, loading } = this.state;

        return (
            <MuiContainer margin="normal" >
                {
                    !loading && data == null
                        ? <AppMessageBox resource="LoadGraphDataFailed" />
                        : this.renderBoxes()
                }
            </MuiContainer>
        );
    }
}

export default KnowledgeTestAnalysisTiles;